'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/menu/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { ChevronRightIcon } from 'BomdisoftComponents/icons/index';
import { OpenInNewRoundedIcon } from 'BomdisoftComponents/icons/open-in-new-rounded';
import { defineMenu, MENU_CHOOSE_EVENT, MENU_CLOSE_EVENT, type MenuElement, type MenuView } from 'BomdisoftComponents/menu/element';
import { MENU_TAG, type MenuRole } from 'BomdisoftComponents/menu/render';
import { createContext, createElement, type MouseEvent, type ReactNode, type RefObject, useCallback, useContext, useEffect, useId, useRef } from 'react';

export type { MenuRole } from 'BomdisoftComponents/menu/render';

defineMenu();

export type MenuProps = {
  align?: 'center' | 'start';
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  anchorRef?: RefObject<HTMLElement | null>;
  anchorPosition?: { left: number; top: number };
  className?: string;
  /** Overrides the generated id, so a trigger outside the popover can name it in
   *  `popovertarget` and be recognised as its invoker rather than an outside click. */
  id?: string;
  /** The popover's accessible name. A `dialog` has no rows to name it, so
   *  without this a screen reader announces an unnamed dialog. */
  label?: string;
  /** Widens the menu to at least its anchor, so a value list is never narrower
   *  than the control it belongs to. */
  matchAnchorWidth?: boolean;
  /** Runs instead of `onClose` when a row inside the menu is what closed it, so a
   *  submenu can take the whole stack down on a choice without doing the same
   *  when the reader merely dismissed it. */
  onChoose?: () => void;
  /** `side` opens the menu beside its anchor instead of below it: the placement
   *  a submenu needs, since one dropped below its row is indistinguishable from
   *  the parent menu's next item. */
  placement?: 'below' | 'side';
  /** `listbox` makes the popover a list of values rather than a list of
   *  commands, so a Select can reuse this popover without lying to a screen
   *  reader about what its rows are. `dialog` is for a popover holding a
   *  composite widget (a calendar, a filter form) rather than rows: a `menu`
   *  whose children are not menuitems is a contract a screen reader cannot
   *  read. */
  role?: MenuRole;
  testId?: string;
};

const MenuRoleContext = createContext<MenuRole>('menu');

/**
 * Gives rows their container's semantics when that container is not a `Menu`.
 *
 * A `MenuItem` takes its role from the enclosing `Menu`, so rows rendered
 * anywhere else default to `menuitem`: a Select whose touch path puts its rows
 * in a bottom sheet ends up with `role="listbox"` around `role="menuitem"`
 * rows. Wrap the rows in this to name what actually encloses them.
 */
export const MenuRoleScope = ({ children, role }: { children: ReactNode; role: MenuRole }) => <MenuRoleContext value={role}>{children}</MenuRoleContext>;

const upgrade = (node: HTMLElement): MenuElement | null => {
  const view = node.ownerDocument.defaultView as MenuView | null;
  defineMenu(view);
  const MenuConstructor = view?.customElements?.get(MENU_TAG);
  return MenuConstructor !== undefined && node instanceof MenuConstructor ? (node as MenuElement) : null;
};

export const Menu = ({ align = 'center', open, onClose, onChoose, children, anchorRef, anchorPosition, className, id: providedId, label, matchAnchorWidth, placement = 'below', role = 'menu', testId }: MenuProps) => {
  const generatedId = useId();
  const element = useRef<MenuElement | null>(null);

  const attach = useCallback(
    (node: HTMLElement | null) => {
      element.current = node === null ? null : upgrade(node);
      if (element.current !== null) element.current.anchorRef = anchorRef ?? null;
    },
    [anchorRef],
  );

  useEffect(() => {
    const menu = element.current;
    if (menu === null) return undefined;
    const handleChoose = (event: Event) => {
      if (event.target === menu) (onChoose ?? onClose)();
    };
    const handleClose = (event: Event) => {
      if (event.target === menu) onClose();
    };
    menu.addEventListener(MENU_CHOOSE_EVENT, handleChoose);
    menu.addEventListener(MENU_CLOSE_EVENT, handleClose);
    return () => {
      menu.removeEventListener(MENU_CHOOSE_EVENT, handleChoose);
      menu.removeEventListener(MENU_CLOSE_EVENT, handleClose);
    };
  }, [onChoose, onClose]);

  return (
    <MenuRoleContext value={role}>
      {createElement(
        MENU_TAG,
        {
          align: align === 'start' ? 'start' : undefined,
          'anchor-left': anchorPosition?.left,
          'anchor-top': anchorPosition?.top,
          'aria-label': label,
          className,
          'data-testid': testId,
          id: providedId ?? generatedId,
          'match-anchor-width': matchAnchorWidth,
          open,
          placement: placement === 'side' ? 'side' : undefined,
          popover: 'auto',
          ref: attach,
          role,
        },
        children,
      )}
    </MenuRoleContext>
  );
};

export type MenuItemProps = {
  children: ReactNode;
  /** Renders an `<a>` instead of a `<button>` — real anchor semantics (open in
   * new tab, crawlability) for a row that navigates rather than acts. */
  href?: string;
  /** For an `href` that leaves the current product's domain: opens in a new
   * tab and appends the conventional trailing open-in-new glyph. */
  external?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  /** Names the row so a trigger's `aria-activedescendant` can point at it. */
  id?: string;
  selected?: boolean;
  /** `'danger'` tints the row (and anything inheriting `currentColor`, like a
   * leading icon) in the error tone — destructive actions: sign out, delete. */
  variant?: 'action' | 'default' | 'danger';
  className?: string;
  testId?: string;
  /** For a row that is a setting rather than a command, so the reader can toggle
   *  several without the menu shutting under them. */
  keepOpenOnClick?: boolean;
};

/**
 * Activating a row applies it and closes the menu.
 *
 * This used to be each consumer's job, and most of them did not do it: picking a
 * project from the switcher left the switcher hanging open over the page. A menu
 * row is a command, so closing is the primitive's default and staying open is the
 * exception a row opts into.
 */
export const MenuItem = ({ children, href, external, id, onClick, disabled, selected, variant = 'default', className, testId, keepOpenOnClick }: MenuItemProps) => {
  const classes = joinClasses('bomdisoft-menu-item', className);
  const dataVariant = variant === 'default' ? undefined : variant;
  const keepOpen = keepOpenOnClick ? '' : undefined;
  const menuRole = useContext(MenuRoleContext);

  if (href) {
    return (
      <a className={classes} data-keep-open={keepOpen} data-testid={testId} data-variant={dataVariant} href={href} id={id} onClick={onClick} rel={external ? 'noopener noreferrer' : undefined} role='menuitem' target={external ? '_blank' : undefined}>
        {children}
        {external ? (
          <span aria-hidden='true' className='bomdisoft-menu-external'>
            <OpenInNewRoundedIcon size={16} />
          </span>
        ) : null}
      </a>
    );
  }
  if (menuRole === 'listbox') {
    return (
      <button aria-selected={Boolean(selected)} className={classes} data-keep-open={keepOpen} data-selected={selected ? '' : undefined} data-testid={testId} data-variant={dataVariant} disabled={disabled} id={id} onClick={onClick} role='option' type='button'>
        {children}
      </button>
    );
  }
  // No role inside a dialog: `menuitem` is only meaningful under a `menu`, and
  // claiming it under a dialog tells a screen reader to expect a menu that the
  // surrounding popover never was.
  return (
    <button className={classes} data-keep-open={keepOpen} data-selected={selected ? '' : undefined} data-testid={testId} data-variant={dataVariant} disabled={disabled} id={id} onClick={onClick} role={menuRole === 'dialog' ? undefined : 'menuitem'} type='button'>
      {children}
    </button>
  );
};

export const MenuDivider = () => <hr className='bomdisoft-menu-divider' />;

export type MenuSubmenuProps = {
  children: ReactNode;
  /** The row's own label, on the left. */
  label: ReactNode;
  /** The chosen value, shown on the row so the reader need not open the submenu
   *  to see what is currently set. */
  value?: ReactNode;
  disabled?: boolean;
  testId?: string;
};

const attachSubmenu = (node: HTMLElement | null) => {
  if (node !== null) upgrade(node);
};

/**
 * A menu row that opens its own menu beside it.
 *
 * The convention this follows, and the reason a nested `<select>` is wrong: a
 * dropdown inside a dropdown opens to the side as a dropdown in its own right.
 * A native select inside a popover renders the platform's own list over the
 * menu, in the platform's own styling, and answers to none of the menu's
 * keyboard or dismissal behaviour.
 *
 * Opens on hover and on click, because a submenu that only answers a click is
 * slower than the list it replaces, and one that only answers hover is
 * unreachable from a keyboard.
 */
/* Both gestures OPEN the submenu; neither toggles it. A toggle fights the hover:
   the pointer opens the submenu on its way to the row, and the click that
   follows would shut the very thing the reader just reached for. Escape, an
   outside click, or choosing a row is what closes it. */
export const MenuSubmenu = ({ children, disabled, label, testId, value }: MenuSubmenuProps) => {
  const submenuId = useId();

  return (
    <>
      {/* popovertarget makes the row the submenu's invoker. Without it the
          light-dismiss algorithm resolves the row's topmost clicked popover to
          the PARENT and hides everything above it, so pressing a row whose
          submenu hover already opened shut that submenu on pointerdown. */}
      <button aria-expanded={false} aria-haspopup='menu' className='bomdisoft-menu-item' data-submenu='' data-testid={testId} disabled={disabled} popoverTarget={submenuId} popoverTargetAction='show' role='menuitem' type='button'>
        <span className='bomdisoft-menu-submenu-label'>{label}</span>
        {value === undefined ? null : <span className='bomdisoft-menu-submenu-value'>{value}</span>}
        <span aria-hidden='true' className='bomdisoft-menu-submenu-chevron'>
          <ChevronRightIcon size={16} />
        </span>
      </button>
      {/* Choosing inside a submenu closes the whole stack: the row was a way in
          to the choice, so leaving the parent open would strand the reader in a
          menu they have finished with. Dismissing it is not choosing, and must
          leave the parent standing. */}
      <MenuRoleContext value='menu'>{createElement(MENU_TAG, { className: 'bomdisoft-menu-submenu', id: submenuId, placement: 'side', popover: 'auto', ref: attachSubmenu, role: 'menu' }, children)}</MenuRoleContext>
    </>
  );
};
